import React, { useState, useEffect } from 'react';
import { salesAPI, productsAPI, customersAPI } from '../services/api';
import './CreateOrderModal.css';

// ✅ Recibimos showToast como prop para usar notificaciones bonitas
const CreateOrderModal = ({ isOpen, onClose, onOrderCreated, showToast }) => {
    // ==================== ESTADOS ====================
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    
    // Búsqueda de clientes
    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    
    // Búsqueda de productos y Carrito
    const [productSearch, setProductSearch] = useState('');
    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [cart, setCart] = useState([]); 
    
    // Datos del pedido
    const [deliveryDate, setDeliveryDate] = useState('');
    const [notes, setNotes] = useState('');
    const [advancePayment, setAdvancePayment] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('efectivo');

    // ==================== EFECTOS ====================
    useEffect(() => {
        if (isOpen) {
            resetForm();
            loadCustomersAndProducts();
        }
    }, [isOpen]);

    useEffect(() => {
        if (customerSearch.length >= 1) {
            const filtered = customers.filter(c =>
                c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                c.phone?.includes(customerSearch)
            );
            setFilteredCustomers(filtered);
        } else {
            setFilteredCustomers([]);
        }
    }, [customerSearch, customers]);

    useEffect(() => {
        if (productSearch.length >= 2) {
            const filtered = allProducts.filter(p =>
                p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                p.code?.toLowerCase().includes(productSearch.toLowerCase())
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts([]);
        }
    }, [productSearch, allProducts]);

    // ==================== FUNCIONES ====================
    const loadCustomersAndProducts = async () => {
        try {
            const customersRes = await customersAPI.getAll();
            setCustomers(customersRes.data.data.customers || []);

            const productsRes = await productsAPI.getAll();
            setAllProducts(productsRes.data.data.products || []);
        } catch (error) {
            console.error('Error al cargar datos:', error);
        }
    };

    const resetForm = () => {
        setStep(1);
        setCustomerSearch('');
        setFilteredCustomers([]);
        setSelectedCustomer(null);
        setProductSearch('');
        setFilteredProducts([]);
        setCart([]);
        setDeliveryDate('');
        setNotes('');
        setAdvancePayment(0);
        setPaymentMethod('efectivo');
    };

    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setCustomerSearch('');
        setFilteredCustomers([]);
        setStep(2);
    };

    // ✅ Reemplazado alert() por showToast()
    const addToCart = (product) => {
        if (product.stock <= 0) {
            showToast(`⚠️ ${product.name} no tiene stock disponible`, 'error');
            return;
        }

        const existingItem = cart.find(item => item.product_id === product.id);

        if (existingItem) {
            if (existingItem.quantity >= product.stock) {
                showToast(`⚠️ Stock máximo alcanzado: ${product.stock}`, 'warning');
                return;
            }
            setCart(cart.map(item =>
                item.product_id === product.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            ));
        } else {
            setCart([...cart, {
                product_id: product.id,
                product_name: product.name,
                unit_price: parseFloat(product.sale_price || 0),
                quantity: 1,
                stock: product.stock 
            }]);
        }

        setProductSearch('');
        setFilteredProducts([]);
    };

    const updateQuantity = (productId, delta) => {
        setCart(prev => prev.map(item => {
            if (item.product_id === productId) {
                const newQty = Math.max(1, item.quantity + delta);
                if (delta > 0 && newQty > item.stock) {
                    showToast(`⚠️ Stock máximo: ${item.stock}`, 'warning');
                    return item;
                }
                return { ...item, quantity: newQty };
            }
            return item;
        }));
    };

    const handleManualQuantity = (productId, value) => {
        let newQty = parseInt(value);
        if (isNaN(newQty) || newQty < 1) newQty = 1;

        setCart(prev => prev.map(item => {
            if (item.product_id === productId) {
                if (newQty > item.stock) {
                    showToast(`⚠️ Stock máximo: ${item.stock}`, 'warning');
                    return { ...item, quantity: item.stock };
                }
                return { ...item, quantity: newQty };
            }
            return item;
        }));
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.product_id !== productId));
    };

    const calculateTotal = () => {
        return cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    };

    const handleSubmit = async () => {
        // ✅ Validaciones con Toast
        if (!selectedCustomer) return showToast('⚠️ Selecciona un cliente', 'warning');
        if (cart.length === 0) return showToast('⚠️ Agrega al menos un producto', 'warning');
        if (!deliveryDate) return showToast('⚠️ Selecciona una fecha de entrega', 'warning');

        setIsLoading(true);

        try {
            const orderData = {
                customer_id: selectedCustomer.id,
                items: cart.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity
                })),
                operation_type: 'pedido',
                delivery_date: deliveryDate,
                notes: notes || '',
                advance_payment: advancePayment > 0 ? advancePayment : 0,
                payment_method: paymentMethod,
                discount_percentage: 0
            };

            const response = await salesAPI.createOrder(orderData);
            
            if (response.data.success) {
                // ✅ Éxito con Toast
                showToast('✅ Pedido creado exitosamente', 'success');
                onOrderCreated(); 
                onClose();
            } else {
                throw new Error(response.data.message || 'Error desconocido al crear');
            }

        } catch (error) {
            console.error('❌ Error al crear pedido:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Error de conexión';
            showToast(`❌ Error: ${errorMsg}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-order-overlay">
            <div className="create-order-modal">
                
                {/* HEADER */}
                <div className="create-order-header">
                    <h2>📋 Crear Nuevo Pedido</h2>
                    <button onClick={onClose} className="close-modal-btn">✕</button>
                </div>

                {/* STEPS */}
                <div className="progress-steps">
                    <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                        <div className="step-number">1</div>
                        <div className="step-label">Cliente</div>
                    </div>
                    <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
                    <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                        <div className="step-number">2</div>
                        <div className="step-label">Productos</div>
                    </div>
                    <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
                    <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                        <div className="step-number">3</div>
                        <div className="step-label">Detalles</div>
                    </div>
                </div>

                {/* CONTENT */}
                <div className="create-order-content">
                    
                    {/* PASO 1: CLIENTE */}
                    {step === 1 && (
                        <div className="step-content">
                            <h3>🔍 Buscar Cliente</h3>
                            {selectedCustomer ? (
                                <div className="selected-customer-card">
                                    <div className="customer-details">
                                        <div className="customer-name">{selectedCustomer.name}</div>
                                        <div className="customer-info">
                                            📞 {selectedCustomer.phone}
                                            {selectedCustomer.email && ` • ✉️ ${selectedCustomer.email}`}
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedCustomer(null)} className="change-customer-btn">Cambiar</button>
                                </div>
                            ) : (
                                <>
                                    <input type="text" placeholder="Buscar por nombre o teléfono..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="search-input-modal" autoFocus />
                                    {filteredCustomers.length > 0 && (
                                        <div className="search-results">
                                            {filteredCustomers.map(customer => (
                                                <div key={customer.id} onClick={() => selectCustomer(customer)} className="search-result-item">
                                                    <div className="result-name">{customer.name}</div>
                                                    <div className="result-info">📞 {customer.phone}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {selectedCustomer && (
                                <button onClick={() => setStep(2)} className="next-step-btn">Siguiente →</button>
                            )}
                        </div>
                    )}

                    {/* PASO 2: PRODUCTOS */}
                    {step === 2 && (
                        <div className="step-content">
                            <h3>🛒 Agregar Productos</h3>
                            <input type="text" placeholder="Buscar productos..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="search-input-modal" />
                            
                            {filteredProducts.length > 0 && (
                                <div className="search-results">
                                    {filteredProducts.map(product => (
                                        <div key={product.id} onClick={() => addToCart(product)} className="search-result-item product-item">
                                            <div className="result-name">{product.name}</div>
                                            <div className="result-info">Stock: {product.unit === 'kg' ? parseFloat(product.stock) : parseInt(product.stock)} • S/. {parseFloat(product.sale_price || 0).toFixed(2)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {cart.length > 0 && (
                                <>
                                    <div className="cart-items">
                                        {cart.map(item => (
                                            <div key={item.product_id} className="cart-item">
                                                <div className="cart-item-info">
                                                    <div className="cart-item-name">{item.product_name}</div>
                                                    <div className="cart-item-price">S/. {item.unit_price.toFixed(2)} x {item.quantity}</div>
                                                </div>
                                                <div className="cart-item-actions">
                                                    <button onClick={() => updateQuantity(item.product_id, -1)} className="quantity-btn">−</button>
                                                    <input 
                                                        type="number"
                                                        className="quantity-input"
                                                        value={item.quantity}
                                                        onChange={(e) => handleManualQuantity(item.product_id, e.target.value)}
                                                        min="1"
                                                    />
                                                    <button onClick={() => updateQuantity(item.product_id, 1)} className="quantity-btn">+</button>
                                                    <button onClick={() => removeFromCart(item.product_id)} className="remove-btn">🗑️</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="cart-total">
                                        <strong>Total:</strong> <span className="total-amount">S/. {calculateTotal().toFixed(2)}</span>
                                    </div>
                                    <div className="step-navigation">
                                        <button onClick={() => setStep(1)} className="back-step-btn">← Volver</button>
                                        <button onClick={() => setStep(3)} className="next-step-btn">Siguiente →</button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* PASO 3: DETALLES */}
                    {step === 3 && (
                        <div className="step-content">
                            <h3>📝 Detalles del Pedido</h3>
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label className="form-label">Fecha de entrega *</label>
                                    <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="form-input" required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Adelanto / Pago inicial</label>
                                    <input type="number" min="0" max={calculateTotal()} step="0.01" value={advancePayment} onChange={(e) => setAdvancePayment(parseFloat(e.target.value) || 0)} placeholder="0.00" className="form-input" />
                                    <div className="quick-payment-btns">
                                        <button onClick={() => setAdvancePayment(calculateTotal() * 0.5)} className="quick-payment-btn">50%</button>
                                        <button onClick={() => setAdvancePayment(calculateTotal())} className="quick-payment-btn">Total</button>
                                    </div>
                                </div>
                                {advancePayment > 0 && (
                                    <div className="form-group">
                                        <label className="form-label">Método de pago</label>
                                        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="form-select">
                                            <option value="efectivo">💵 Efectivo</option>
                                            <option value="tarjeta">💳 Tarjeta</option>
                                            <option value="transferencia">🏦 Transferencia</option>
                                        </select>
                                    </div>
                                )}
                                <div className="form-group full-width">
                                    <label className="form-label">Notas</label>
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" className="form-textarea" placeholder="Instrucciones..."></textarea>
                                </div>
                            </div>
                            <div className="order-summary">
                                <div className="summary-row"><span>Total:</span><strong>S/. {calculateTotal().toFixed(2)}</strong></div>
                                {advancePayment > 0 && (
                                    <>
                                        <div className="summary-row"><span>Adelanto:</span><span className="advance-amount">S/. {advancePayment.toFixed(2)}</span></div>
                                        <div className="summary-row balance"><span>Saldo:</span><strong>S/. {(calculateTotal() - advancePayment).toFixed(2)}</strong></div>
                                    </>
                                )}
                            </div>
                            <div className="step-navigation">
                                <button onClick={() => setStep(2)} className="back-step-btn">← Atrás</button>
                                <button onClick={handleSubmit} disabled={isLoading || !deliveryDate} className="submit-order-btn">{isLoading ? '⏳ Creando...' : '✅ Crear Pedido'}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateOrderModal;